/**
 * Entity line-style resolution (docs/plans/2d/2d-full-parity-plan.md
 * Faz 4): per-entity linetype / lineweight / transparency with layer-level
 * fallback, following 2D's BYLAYER semantics.
 *
 * Entity data-bag fields (all optional):
 *  - linetype: 'DASHED' | 'CENTER' | 'HIDDEN' | 'DOTTED' | 'PHANTOM' |
 *              'DASHDOT' | 'BYLAYER' | undefined
 *  - lineWeight: pixel width override (1-6)
 *  - transparency: 0-100 percent
 * Layers carry the same names (linetype?/lineweight?/transparency?).
 */

import type { Entity, Layer } from '../state/store';

export interface ResolvedLineStyle {
    /** [dashSize, gapSize] world units, or null for continuous. */
    dash: [number, number] | null;
    /** Rendered pixel width (drei lineWidth). */
    width: number;
    /** 0-1 opacity. */
    opacity: number;
}

/** Linetype patterns in world units at unit LTSCALE (pattern period varies
 *  by type; one dash/gap pair approximates the classic look). */
const LINETYPE_DASHES: Record<string, [number, number]> = {
    DASHED: [0.5, 0.3],
    HIDDEN: [0.35, 0.25],
    DOTTED: [0.02, 0.18],
    CENTER: [0.8, 0.25],
    PHANTOM: [0.9, 0.2],
    DASHDOT: [0.6, 0.2],
};

const BYLAYER = (v: string | undefined): boolean =>
    !v || v === 'BYLAYER' || v === 'BYBLOCK';

/** Viewport display overrides (Faz 4.2 status-bar consumers): LWDISPLAY
 *  off renders every line hairline; transparency display off forces opaque. */
export interface LineStyleDisplay {
    showLineweights?: boolean;
    showTransparency?: boolean;
}

export function entityLineStyle(
    entity: Entity,
    layer?: Layer,
    display?: LineStyleDisplay
): ResolvedLineStyle {
    const d = entity.data as Record<string, any>;
    const rawType = BYLAYER(d.linetype as string | undefined)
        ? layer?.linetype
        : (d.linetype as string);
    const dash = rawType ? LINETYPE_DASHES[String(rawType).toUpperCase()] ?? null : null;

    const rawLw = BYLAYER(d.lineWeight as string | undefined)
        ? layer?.lineweight
        : (d.lineWeight as number);
    // LWT pill off (LWDISPLAY=0): stored lineweights are ignored and every
    // line draws at the hairline width — the classic display toggle.
    const showLw = display?.showLineweights ?? true;
    const width = showLw && typeof rawLw === 'number' && rawLw >= 1 ? Math.min(8, 1 + rawLw * 0.6) : 1;

    const rawTr = typeof d.transparency === 'number'
        ? d.transparency
        : layer?.transparency;
    // Transparency pill off ("force opaque"): opacity overrides to 1.
    const showTr = display?.showTransparency ?? true;
    const opacity = !showTr
        ? 1
        : typeof rawTr === 'number'
            ? Math.max(0.05, 1 - Math.min(100, Math.max(0, rawTr)) / 100)
            : 1;

    return { dash, width, opacity };
}

/** Extra props spread onto drei <Line> for a resolved style. */
export function lineStyleProps(style: ResolvedLineStyle): Record<string, unknown> {
    return {
        lineWidth: style.width,
        dashed: !!style.dash,
        ...(style.dash ? { dashSize: style.dash[0], gapSize: style.dash[1] } : {}),
        transparent: style.opacity < 1,
        opacity: style.opacity,
    };
}
